import {isNotNil} from 'es-toolkit';
import {inject, injectable} from 'inversify';
import {toJS} from 'mobx';

import {i18n} from '@yourcoach/i18n';

import type {Expanded} from '../../../../api/types';
import type {BNUserProfileUpdateParams} from '../../../../api/types/bnrks/methods';
import type {CoreUserProfileUpdateParams} from '../../../../api/types/core/methods';
import type {UserApiModel, UserExpandedCommon} from '../../../../api/user';
import type {IApiDataMapper} from '../../../ports/types';
import type {UserModel} from '../User.model';

import {valueOrNull} from '../../../../utils/valueOrNull';
import {EnvProviderService} from '../../../system/env/EnvProvider.service';
import {CategoryMapperJrpc} from '../../Category/mappers/Category.mapper.jrpc';
import {PrivateProfileMapperJrpc} from '../../common/PrivateProfile/mappers/PrivateProfile.mapper.jrpc';
import {UserAdditionalDataMapperJrpc} from '../../common/UserAdditionalData/mappers/UserAdditionalData.mapper.jrpc';
import {DateTimeModelFactory} from '../../Date/DateTime.factory';
import {FileFactory} from '../../File/File.factory';
import {FileMapperJrpc} from '../../File/mappers/File.mapper.jrpc';
import {SubtenantMapperJrpc} from '../../Subtenant/mappers/Subtenant.mapper.jrpc';
import {UserFactory} from '../User.factory';
import {UserMetadataMapperJrpc} from '../UserMetadata/mappers/UserMetadata.mapper.jrpc';
import {UserOnboardingMapperJrpc} from '../UserOnboarding/mappers/UserOnboarding.mapper.jrpc';
import {UserExpandJrpc} from './User.expand.jrpc';

@injectable()
export class UserMapperJrpc
  implements IApiDataMapper<UserMapperJrpc.ApiData, UserModel.Props, UserModel>
{
  constructor(
    @inject(UserFactory)
    private readonly factory: UserFactory,
    @inject(UserExpandJrpc)
    public readonly expand: UserExpandJrpc,
    @inject(FileMapperJrpc) private readonly fileMapper: FileMapperJrpc,
    @inject(FileFactory) private readonly fileFactory: FileFactory,
    @inject(DateTimeModelFactory)
    private readonly dateTimeFactory: DateTimeModelFactory,
    @inject(PrivateProfileMapperJrpc)
    private readonly privateProfileMapper: PrivateProfileMapperJrpc,
    @inject(SubtenantMapperJrpc)
    private readonly subtenantMapper: SubtenantMapperJrpc,
    @inject(UserAdditionalDataMapperJrpc)
    private readonly additionalDataMapper: UserAdditionalDataMapperJrpc,
    @inject(EnvProviderService) private readonly env: EnvProviderService,
    @inject(UserMetadataMapperJrpc)
    private readonly metadataMapper: UserMetadataMapperJrpc,
    @inject(CategoryMapperJrpc)
    private readonly categoryMapper: CategoryMapperJrpc,
    @inject(UserOnboardingMapperJrpc)
    private readonly onboardingMapper: UserOnboardingMapperJrpc,
  ) {}

  mapDataToModel(apiData: UserMapperJrpc.ExpandedApiData): UserModel {
    return this.factory.create(this.mapDataToProps(apiData));
  }

  mapDataToModelOrEmpty(apiData?: Expanded<UserExpandedCommon, 'name'>) {
    if (!apiData?._id) {
      return this.factory.createEmpty({
        name: apiData?.name || i18n.t('shared:user_not_found'),
      });
    }

    return this.mapDataToModel(apiData as UserExpandedCommon);
  }

  mapDataToProps(apiData: UserMapperJrpc.ExpandedApiData): UserModel.Props {
    const id = apiData._id;
    const isDeleted = this.isDeleted(apiData);
    const roles = this.roles(apiData);
    const slug = this.slug(apiData);
    const coachTitle = this.coachTitle(apiData);
    const coachMaxClients = apiData.coach_max_number_of_clients;
    const externalId = this.externalId(apiData);
    const isCoach = this.hasRole(apiData, 'coach');
    const isCorporateCoach = this.hasRole(apiData, 'approved_corporate_coach');
    const isSupport = this.hasRole(apiData, 'support');
    const name = this.name({apiData, isDeleted});
    const avatar = this.avatar(apiData);
    const coachLogo = this.coachLogo(apiData);
    const description = this.description(apiData, isCoach);
    const lastSeenAt = this.lastSeenAt(apiData);
    const gender = this.gender(apiData);
    const location = this.location(apiData);
    const timezone = this.timezone(apiData);
    const privateProfile = this.privateProfile(apiData);
    const source = this.source(apiData, isCoach);
    const subtenant = this.subtenant(apiData);
    const yearOfBirth = this.yearOfBirth(apiData);
    const metadata = this.metadata(apiData);
    const accountType = this.accountType(apiData);
    const additionalData = this.additionalData(apiData);

    const coachSubtenants = this.coachSubtenants(apiData);
    const populations = this.populations(apiData);
    const onboarding = this.onboarding(apiData);
    const createdAt = this.createdAt(apiData);
    const visible_widgets = this.visibleWidgets(apiData);

    return {
      accountType,
      additionalData,
      apiData,
      avatar,
      coachLogo,
      coachMaxClients,
      coachSubtenants,
      coachTitle,
      createdAt,
      description,
      externalId,
      gender,
      id,
      isCoach,
      isCorporateCoach,
      isDeleted,
      isSupport,
      lastSeenAt,
      location,
      metadata,
      name,
      onboarding,
      populations,
      privateProfile,
      roles,
      slug,
      source,
      subtenant,
      timezone,
      visible_widgets,
      yearOfBirth,
    };
  }

  mapModelToDto(model: UserModel): UserMapperJrpc.Dto {
    const private_profile = this.privateProfileMapper.mapModelToDto(
      model.privateProfile,
    );
    const metadata = this.metadataMapper.mapModelToDto(model.metadata);
    const onboardingData = this.onboardingMapper.mapModelToDto(
      model.onboarding,
    );

    const data = toJS(model);

    let dto: UserMapperJrpc.Dto = {
      avatar_id: valueOrNull(data.avatar?.id),
      client_description: valueOrNull(data.description),
      coach_description: valueOrNull(data.description),
      coach_logo_id: valueOrNull(data.coachLogo?.id),
      coach_title: valueOrNull(data.coachTitle),
      expand: this.expand.getByEnv(this.env.app.isB2b ? 'b2b' : 'core'),
      gender: data.gender,
      location: data.location,
      metadata,
      name: data.name,
      private_profile,
      slug: valueOrNull(data.slug),
      timezone: data.timezone ?? undefined,
      year_of_birth: data.yearOfBirth,
    };

    if (this.env.app.isB2b) {
      dto = {
        ...dto,
        additional_data: data.additionalData
          ? this.additionalDataMapper.mapModelToDto(data.additionalData)
          : null,
      };
    }

    if (this.env.app.isCore) {
      dto = {
        ...dto,
        onboarding: data.onboarding ? onboardingData : null,
        visible_widgets: data.visible_widgets ?? [],
      };
    }

    return dto;
  }

  private accountType(
    apiData: UserMapperJrpc.ApiData,
  ): UserModel.Props['accountType'] {
    return apiData.metadata?.onboarding?.account_type ?? null;
  }

  private additionalData(
    apiData: UserMapperJrpc.ExpandedApiData,
  ): UserModel.Props['additionalData'] {
    const data = this.additionalDataForByAccountType(apiData);

    if (!data) {
      return null;
    }

    return this.additionalDataMapper.mapDataToModel(data);
  }

  private additionalDataForByAccountType(
    apiData: UserMapperJrpc.ExpandedApiData,
  ) {
    // TODO: remove when backend sets additional_data for employees
    let data =
      apiData.private_profile?.additional_data || apiData.additional_data || [];

    const isEmployee =
      apiData.metadata?.onboarding?.account_type === 'employee';

    const isB2cDemoUser = apiData.metadata?.b2c_demo?.code;
    const isDtcUser = apiData.metadata?.is_dtc;
    // Not empty object
    const isDtcQrCodeUser =
      isDtcUser &&
      Boolean(Object.keys(apiData.metadata.dtc_qrcode ?? {}).length);

    const shouldRenderEmployeeAnswers =
      (isEmployee || isB2cDemoUser) && !isDtcQrCodeUser;
    const shouldRenderDtcQrCodeAnswers = isDtcQrCodeUser;

    if (shouldRenderEmployeeAnswers && apiData.metadata.employee) {
      const questions = {
        anxious: i18n.t('onboarding:anxious_title'),
        anything_else: i18n.t('onboarding:anything_else_to_know'),
        coaching_style: i18n.t('onboarding:coaching_style'),
        support: i18n.t('onboarding:ideal_support'),
        what_brings_you: i18n.t('onboarding:what_brings_you_to_coaching'),
        worrying: i18n.t('onboarding:worrying_title'),
      };

      data = Object.entries(apiData.metadata.employee)
        .filter(([_, answer]) => Boolean(answer))
        .map(([question, answer]) => ({
          answer,
          question: questions[question],
        }));
    }

    if (shouldRenderDtcQrCodeAnswers && apiData.metadata.dtc_qrcode) {
      const dtc = apiData.metadata.dtc_qrcode;

      data = [
        {
          answer: dtc.age,
          question: i18n.t('onboarding:age_title'),
        },
        {
          answer: dtc.areas_of_wellbeing?.join(', '),
          question: i18n.t('onboarding:areas_to_improve_title'),
        },
        {
          answer: dtc.obstacles?.join(', '),
          question: i18n.t('onboarding:obstacles_title'),
        },
        {
          answer: dtc.coaching_style,
          question: i18n.t('onboarding:coaching_style_title'),
        },
        {
          answer: dtc.other_professionals?.join(', '),
          question: i18n.t('onboarding:other_professionals_title'),
        },
        {
          answer: dtc.anxious,
          question: i18n.t('onboarding:anxious_title'),
        },
        {
          answer: dtc.worrying,
          question: i18n.t('onboarding:worrying_title'),
        },
      ];
    }

    if (apiData.onboarding?.answers.length) {
      const onboardingData = apiData.onboarding.answers.map(item => ({
        answer: item.answer.join(', '),
        question: item.question,
      }));

      data = data.concat(onboardingData);
    }

    return data;
  }

  private avatar(
    props: UserMapperJrpc.ExpandedApiData,
  ): UserModel.Props['avatar'] {
    if (props.avatar) {
      const modelProps = this.fileMapper.mapDataToProps(props.avatar);

      return this.fileFactory.create(modelProps);
    }

    return null;
  }

  private coachLogo(
    props: UserMapperJrpc.ExpandedApiData,
  ): UserModel.Props['coachLogo'] {
    if (props.coach_logo) {
      const modelProps = this.fileMapper.mapDataToProps(props.coach_logo);

      return this.fileFactory.create(modelProps);
    }

    return null;
  }

  private coachSubtenants(
    props: UserMapperJrpc.ExpandedApiData,
  ): UserModel.Props['coachSubtenants'] {
    if (!props.coach_subtenants) {
      return [];
    }

    return props.coach_subtenants
      .filter(s => Boolean(s))
      .map(subtenant =>
        this.subtenantMapper.mapExpandedToModel(
          subtenant as NonNullable<typeof subtenant>,
        ),
      );
  }

  private coachTitle(
    props: UserMapperJrpc.ApiData,
  ): UserModel.Props['coachTitle'] {
    return props.coach_title ?? '';
  }

  private createdAt(
    apiData: UserMapperJrpc.ApiData,
  ): UserModel.Props['createdAt'] {
    return this.dateTimeFactory.createFromDateTimeObject(apiData.created);
  }

  private description(
    props: UserMapperJrpc.ApiData,
    isCoach: boolean,
  ): UserModel.Props['description'] {
    const description = isCoach
      ? props.coach_description
      : props.client_description;

    return description ?? '';
  }

  private externalId(
    props: UserMapperJrpc.ApiData,
  ): UserModel.Props['externalId'] {
    return valueOrNull(props.external_id);
  }

  private gender(apiData: UserMapperJrpc.ApiData): UserModel.Props['gender'] {
    return apiData.gender ?? null;
  }

  private hasRole(
    props: UserMapperJrpc.ApiData,
    role: UserMapperJrpc.ApiData['roles'][0],
  ): boolean {
    if (!props.roles) {
      return false;
    }

    return props.roles.includes(role);
  }

  private isDeleted(
    props: UserMapperJrpc.ApiData,
  ): UserModel.Props['isDeleted'] {
    return Boolean(props.deleted);
  }

  private lastSeenAt(
    apiData: UserMapperJrpc.ApiData,
  ): UserModel.Props['lastSeenAt'] {
    const lastSeen = apiData.last_seen;

    return lastSeen
      ? this.dateTimeFactory.createFromDateTimeObject(lastSeen)
      : null;
  }

  private location(props: UserMapperJrpc.ApiData): UserModel.Props['location'] {
    return valueOrNull(props.location);
  }

  private metadata(props: UserMapperJrpc.ApiData): UserModel.Props['metadata'] {
    return this.metadataMapper.mapDataToModel(props.metadata);
  }

  private name(props: {
    apiData: UserMapperJrpc.ApiData;
    isDeleted: boolean;
  }): UserModel.Props['name'] {
    const {apiData, isDeleted} = props;

    return isDeleted
      ? i18n.t('shared:deleted_user_name')
      : (apiData.name ?? i18n.t('shared:user_not_found'));
  }

  private onboarding(
    props: UserMapperJrpc.ApiData,
  ): UserModel.Props['onboarding'] {
    return this.onboardingMapper.mapDataToModel(props.onboarding);
  }

  private populations(
    props: UserMapperJrpc.ExpandedApiData,
  ): UserModel.Props['populations'] {
    if (!props.populations) {
      return [];
    }

    return props.populations
      .filter(isNotNil)
      .map(population => this.categoryMapper.mapDataToModel(population));
  }

  private privateProfile(
    props: UserMapperJrpc.ApiData,
  ): UserModel.Props['privateProfile'] {
    const privateProfileData = props.private_profile ?? {};

    return this.privateProfileMapper.mapDataToModel(privateProfileData);
  }

  private roles(props: UserMapperJrpc.ApiData): UserModel.Props['roles'] {
    return props.roles ?? [];
  }

  private slug(props: UserMapperJrpc.ApiData): UserModel.Props['slug'] {
    return props.slug ?? '';
  }

  // NOTE: Keep in sync with getUserSource in utils/entity/user/getUserSource.ts
  private source(
    apiData: UserMapperJrpc.ApiData,
    isCoach: boolean,
  ): UserModel.Props['source'] {
    if (this.env.app.isB2b) {
      return isCoach ? 'direct' : 'b2b_widget';
    }

    return apiData.onboarding?.type === 'external' ? 'b2b_widget' : 'direct';
  }

  private subtenant(
    props: UserMapperJrpc.ExpandedApiData,
  ): UserModel.Props['subtenant'] {
    if (props.subtenant) {
      return this.subtenantMapper.mapExpandedToModel(props.subtenant);
    }

    return null;
  }

  private timezone(
    props: UserMapperJrpc.ExpandedApiData,
  ): UserModel.Props['timezone'] {
    return valueOrNull(props.timezone);
  }

  private visibleWidgets(
    props: UserMapperJrpc.ApiData,
  ): UserModel.Props['visible_widgets'] {
    const list = props.visible_widgets;

    if (!Array.isArray(list)) {
      return [];
    }

    return list;
  }

  private yearOfBirth(
    props: UserMapperJrpc.ExpandedApiData,
  ): UserModel.Props['yearOfBirth'] {
    return valueOrNull(props.year_of_birth);
  }
}

export namespace UserMapperJrpc {
  export type ApiData = UserApiModel;

  export type Dto = BNUserProfileUpdateParams | CoreUserProfileUpdateParams;

  export type ExpandedApiData = ApiData & UserExpandJrpc.ExpandedFields;
}
